/* ============================================================
   Blog Carousel — section-blog-carousel.js
   Transform-based carousel. No external deps.
   ============================================================ */
(function () {
  'use strict';

  const SELECTOR = '[data-blog-carousel]';
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BlogCarousel {
    constructor(root) {
      this.root       = root;
      this.viewport   = root.querySelector('[data-carousel-viewport]');
      this.track      = root.querySelector('[data-carousel-track]');
      this.cards      = Array.from(root.querySelectorAll('[data-carousel-card]'));
      this.prevBtns   = Array.from(root.querySelectorAll('[data-carousel-prev]'));
      this.nextBtns   = Array.from(root.querySelectorAll('[data-carousel-next]'));
      this.dotsWrap   = root.querySelector('[data-carousel-dots]');
      this.dots       = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('[data-carousel-dot]')) : [];

      this.currentIndex  = 0;
      this.totalCards    = this.cards.length;
      this.autoPlayTimer = null;

      this.colsDesktop = parseInt(root.dataset.colsD, 10) || 3;
      this.colsTablet  = parseInt(root.dataset.colsT, 10) || 2;
      this.colsMobile  = parseInt(root.dataset.colsM, 10) || 1;
      this.autoplay    = root.dataset.autoplay === 'true';
      this.autoSpeed   = (parseInt(root.dataset.autoplaySpeed, 10) || 4) * 1000;

      if (!this.track || this.cards.length === 0) return;
      this.init();
    }

    /* ── Helpers ─────────────────────────────────────────────── */

    getSlidesPerView() {
      const w = window.innerWidth;
      if (w >= 990) return this.colsDesktop;
      if (w >= 750) return this.colsTablet;
      return this.colsMobile;
    }

    maxIndex() {
      return Math.max(0, this.totalCards - this.getSlidesPerView());
    }

    /* Card width in px (including gap offset per card) */
    getStepWidth() {
      const card = this.cards[0];
      if (!card) return 0;
      const gap = parseFloat(
        getComputedStyle(this.track).getPropertyValue('gap')
      ) || 0;
      return card.offsetWidth + gap;
    }

    /* ── Move ────────────────────────────────────────────────── */

    goTo(index, instant) {
      const max = this.maxIndex();
      this.currentIndex = Math.min(Math.max(index, 0), max);
      const offset = -(this.currentIndex * this.getStepWidth());

      if (prefersReduced || instant) {
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${offset}px)`;
        // re-enable transition after forced reflow
        this.track.offsetHeight; // eslint-disable-line no-unused-expressions
        this.track.style.transition = '';
      } else {
        this.track.style.transform = `translateX(${offset}px)`;
      }

      this.updateArrows();
      this.updateDots();
    }

    prev() { this.goTo(this.currentIndex - 1); this.resetAutoPlay(); }
    next() { this.goTo(this.currentIndex + 1); this.resetAutoPlay(); }

    /* ── State ───────────────────────────────────────────────── */

    updateArrows() {
      const atStart = this.currentIndex <= 0;
      const atEnd   = this.currentIndex >= this.maxIndex();
      this.prevBtns.forEach(btn => {
        btn.disabled = atStart;
        btn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      });
      this.nextBtns.forEach(btn => {
        btn.disabled = atEnd;
        btn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      });
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
      });
    }

    /* ── Auto-play ───────────────────────────────────────────── */

    startAutoPlay() {
      if (!this.autoplay || prefersReduced) return;
      this.autoPlayTimer = setInterval(() => {
        if (this.currentIndex >= this.maxIndex()) {
          this.goTo(0);
        } else {
          this.next();
        }
      }, this.autoSpeed);
    }

    resetAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.startAutoPlay();
      }
    }

    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.autoPlayTimer = null;
      }
    }

    /* ── Touch / swipe ───────────────────────────────────────── */

    initTouch() {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      this.viewport.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
      }, { passive: true });

      this.viewport.addEventListener('touchmove', e => {
        if (!isDragging) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 6) {
          e.preventDefault();
        }
      }, { passive: false });

      this.viewport.addEventListener('touchend', e => {
        if (!isDragging) return;
        isDragging = false;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) {
          dx < 0 ? this.next() : this.prev();
        }
      }, { passive: true });
    }

    /* ── Keyboard ────────────────────────────────────────────── */

    initKeyboard() {
      this.root.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { this.prev(); e.preventDefault(); }
        if (e.key === 'ArrowRight') { this.next(); e.preventDefault(); }
      });
    }

    /* ── Resize ──────────────────────────────────────────────── */

    initResize() {
      let timeout;
      this._handleResize = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          const max = this.maxIndex();
          if (this.currentIndex > max) {
            this.goTo(max, true);
          } else {
            this.goTo(this.currentIndex, true);
          }
        }, 150);
      };
      window.addEventListener('resize', this._handleResize, { passive: true });
    }

    /* ── Visibility pause ────────────────────────────────────── */

    initVisibility() {
      this._onVisibility = () => {
        if (document.hidden) {
          this.stopAutoPlay();
        } else {
          this.startAutoPlay();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      this.root.addEventListener('mouseenter', () => this.stopAutoPlay());
      this.root.addEventListener('mouseleave', () => this.startAutoPlay());
    }

    /* ── Init ────────────────────────────────────────────────── */

    init() {
      // Wire up arrows
      this.prevBtns.forEach(btn => btn.addEventListener('click', () => this.prev()));
      this.nextBtns.forEach(btn => btn.addEventListener('click', () => this.next()));

      // Wire up dots
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.goTo(i);
          this.resetAutoPlay();
        });
      });

      this.initTouch();
      this.initKeyboard();
      this.initResize();
      this.initVisibility();

      // Set initial state
      this.goTo(0, true);
      this.startAutoPlay();
    }

    /* ── Destroy ─────────────────────────────────────────────── */

    destroy() {
      this.stopAutoPlay();
      window.removeEventListener('resize', this._handleResize);
      document.removeEventListener('visibilitychange', this._onVisibility);
    }
  }

  /* ── Boot ──────────────────────────────────────────────────── */

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll(SELECTOR).forEach(el => {
      if (!el.__blogCarousel) {
        el.__blogCarousel = new BlogCarousel(el);
      }
    });
  }

  function destroyAll(scope) {
    const root = scope || document;
    root.querySelectorAll(SELECTOR).forEach(el => {
      if (el.__blogCarousel) {
        el.__blogCarousel.destroy();
        delete el.__blogCarousel;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* ── Shopify Theme Editor ──────────────────────────────────── */
  document.addEventListener('shopify:section:load',   e => initAll(e.target));
  document.addEventListener('shopify:section:unload', e => destroyAll(e.target));
  document.addEventListener('shopify:section:select', e => {
    const inst = e.target.querySelector(SELECTOR)?.__blogCarousel;
    if (inst) inst.goTo(0, true);
  });
})();
